// QuoteDocuments.js
//
// ONE shared "sent documents" panel used by EVERY quote builder (Managed IT,
// Voice, Bundle, Multi-Site, FlexIT). Reads the quote's signwellDocuments and
// lists every document that has gone out — Service Order / agreement, LOA,
// International Dialing Waiver, ETF letter — with its status, recipient, date,
// and a download of the signed copy. Type-agnostic on purpose: the send/signature
// UI differs by quote type, but *seeing and downloading what was sent* is
// identical everywhere, so it lives in one component (do NOT fork this per type).
//
// Download is reconcile-aware: if a doc is signed/completed but its signed-PDF URL
// hasn't been captured yet (e.g. a missed SignWell webhook), the download button
// runs the server reconcile first (which also finalizes the HubSpot attachment +
// team email + Closed-Won move), then opens the PDF.
import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { reconcileCompletion, refreshDocStatus } from '../lib/signatureDocs';
import { SubCard } from './v3';
import { v3 } from '../lib/styleTokens';

const DOC_LABELS = {
  voice_quote:      'Voice Service Agreement',
  bundle_quote:     'Bundle Service Agreement',
  flexit_quote:     'FlexIT Service Agreement',
  managed_it_quote: 'Managed IT Service Agreement',
  service_order:    'Service Order',
  loa:              'Letter of Authorization',
  intl_waiver:      'International Dialing Waiver',
  etf_letter:       'Early Termination Letter',
};
const docLabel = (t) => DOC_LABELS[t] || 'Document';

const STATUS_STYLE = {
  pdf_generated: { bg: '#f1f5f9', color: '#475569', label: 'PDF generated' },
  draft:         { bg: '#f1f5f9', color: '#475569', label: 'Draft' },
  sent:          { bg: '#dbeafe', color: '#1e40af', label: 'Sent — awaiting signature' },
  viewed:        { bg: '#fef3c7', color: '#92400e', label: 'Viewed by client' },
  signed:        { bg: '#dcfce7', color: '#166534', label: 'Signed' },
  completed:     { bg: '#dcfce7', color: '#166534', label: 'Signed & complete' },
  declined:      { bg: '#fee2e2', color: '#991b1b', label: 'Declined' },
  cancelled:     { bg: '#f3f4f6', color: '#6b7280', label: 'Cancelled' },
  expired:       { bg: '#fef3c7', color: '#92400e', label: 'Expired' },
};
const statusStyle = (s) => STATUS_STYLE[String(s || '').toLowerCase()] || STATUS_STYLE.sent;
const isDownloadable = (s) => ['signed', 'completed'].includes(String(s || '').toLowerCase());

export default function QuoteDocuments({ quoteId }) {
  const [docs, setDocs]   = useState(null); // null = loading
  const [busyId, setBusy] = useState(null);
  const [msg, setMsg]     = useState('');

  const load = useCallback(async () => {
    if (!quoteId) { setDocs([]); return; }
    const { data } = await supabase.from('quotes').select('inputs').eq('id', quoteId).single();
    const list = Array.isArray(data?.inputs?.signwellDocuments) ? data.inputs.signwellDocuments : [];
    // Newest first.
    setDocs([...list].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
  }, [quoteId]);

  useEffect(() => { load(); }, [load]);

  async function handleDownload(doc) {
    if (doc.completed_pdf_url) { window.open(doc.completed_pdf_url, '_blank', 'noopener'); return; }
    setBusy(doc.id); setMsg('');
    try {
      const r = await reconcileCompletion(doc.id);
      await load();
      if (r?.completed_pdf_url) window.open(r.completed_pdf_url, '_blank', 'noopener');
      else setMsg('Signed PDF isn’t available from SignWell yet — try again shortly.');
    } catch (e) {
      setMsg('Could not fetch the signed PDF: ' + (e?.message || 'unknown error'));
    } finally { setBusy(null); }
  }

  async function handleRefresh(doc) {
    setBusy(doc.id); setMsg('');
    try { await refreshDocStatus(quoteId, doc.id); await load(); }
    catch (e) { setMsg('Refresh failed: ' + (e?.message || 'unknown error')); }
    finally { setBusy(null); }
  }

  const body = () => {
    if (!quoteId) return <Empty text="Save the quote first to track sent documents." />;
    if (docs === null) return <Empty text="Loading documents…" />;
    if (!docs.length) return <Empty text="No documents have been sent for this quote yet." />;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {docs.map((doc, i) => {
          const st = statusStyle(doc.status);
          const when = doc.created_at ? new Date(doc.created_at).toLocaleString() : '';
          const busy = busyId === doc.id;
          return (
            <div key={doc.id || i} style={{ border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.card, padding: '10px 12px', background: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: v3.color.ink }}>{docLabel(doc.type)}</span>
                <span style={{ padding: '2px 8px', borderRadius: v3.radius.pill, fontSize: 10, fontWeight: 700, background: st.bg, color: st.color }}>{st.label}</span>
                {doc.test_mode && <span style={{ padding: '2px 7px', borderRadius: v3.radius.pill, fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#a16207' }}>TEST</span>}
              </div>
              <div style={{ fontSize: 11, color: v3.color.muted, marginTop: 4, lineHeight: 1.6 }}>
                {doc.client_email && <div>To: {doc.client_email}</div>}
                {when && <div>Sent {when}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {isDownloadable(doc.status) && (
                  <button onClick={() => handleDownload(doc)} disabled={busy} style={btnLink}>
                    {busy ? 'Fetching…' : '📄 Download Signed PDF'}
                  </button>
                )}
                {String(doc.status || '').toLowerCase() !== 'cancelled' && (
                  <button onClick={() => handleRefresh(doc)} disabled={busy} style={btnSecondary}>↻ Refresh</button>
                )}
              </div>
            </div>
          );
        })}
        {msg && <div style={{ fontSize: 11, color: msg.startsWith('Could') || msg.includes('failed') ? '#b91c1c' : v3.color.muted }}>{msg}</div>}
      </div>
    );
  };

  return <SubCard eyebrow="Sent documents">{body()}</SubCard>;
}

function Empty({ text }) {
  return <div style={{ fontSize: 12, color: v3.color.muted }}>{text}</div>;
}

const btnSecondary = {
  padding: '6px 12px', background: '#fff', color: '#374151',
  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
};
const btnLink = {
  padding: '6px 12px', background: 'transparent', color: '#166534',
  border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
};
